import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { attrRicerca } from "@/lib/listaColonne";
import { ChevronDown, X } from "lucide-react";
import { STATI_OPP, STATI_PREDEFINITI } from "./crmComuni";

// Sprint CRM-1.1 — filtri CRM. UNA regola: campi diversi «e», valori nello stesso campo «o». Lavorano sul server.
export const FILTRI_VUOTI = { stati: STATI_PREDEFINITI, titolari: [], fonti: [], fasi: [], etichette: [], motivi: [], oltre_avviso: "",
  senza_attivita_da: "", creata_dal: "", creata_al: "", chiusa_dal: "", chiusa_al: "", veicolo: "", q: "" };

// Sprint CRM-5 — scorciatoie dei periodi calcolate dal server con la stessa funzione della dashboard (tempo.periodo_scorciatoia)
export const periodo = (scorciatoia, periodi) => periodi?.[scorciatoia] || null;

export function queryFiltri(f, vista) {
  const p = new URLSearchParams();
  p.set("stati", f.stati.join(","));
  for (const k of ["titolari", "fonti", "etichette", "motivi"]) if (f[k]?.length) p.set(k, f[k].join(","));
  if (f.oltre_avviso) p.set("oltre_avviso", f.oltre_avviso);
  if (vista === "righe" && Number(f.senza_attivita_da) >= 1) p.set("senza_attivita_da", String(Number(f.senza_attivita_da)));
  if (vista === "righe" && f.fasi.length) p.set("fasi", f.fasi.join(","));
  for (const k of ["creata_dal", "creata_al", "chiusa_dal", "chiusa_al", "veicolo"]) if (f[k]) p.set(k, f[k]);
  if (f.q.trim()) p.set("q", f.q.trim());
  return p;
}

function Multi({ label, scelte, valori, onCambia, testid }) {
  const tog = (id) => onCambia(valori.includes(id) ? valori.filter((x) => x !== id) : [...valori, id]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testid}>{label}{valori.length ? ` (${valori.length})` : ""}<ChevronDown className="h-3 w-3 ml-1" /></Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 max-h-72 overflow-auto p-2 space-y-1">
        {scelte.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm">
            <Checkbox checked={valori.includes(s.id)} onCheckedChange={() => tog(s.id)} data-testid={`${testid}-${s.id}`} />{s.nome}
          </label>
        ))}
        {!scelte.length && <div className="text-xs text-slate-400">Nessuna scelta</div>}
      </PopoverContent>
    </Popover>
  );
}

function Periodo({ label, dal, al, onCambia, testid, periodi }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-sm" data-testid={testid}>
      <span className="text-slate-600">{label}</span>
      <Select value="_" onValueChange={(v) => { const r = periodo(v, periodi); if (r) onCambia(...r); }}>
        <SelectTrigger className="h-8 w-36" data-testid={`${testid}-scorciatoia`}><SelectValue placeholder="Periodo" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_" disabled>Dal… al…</SelectItem>
          <SelectItem value="mese">Mese corrente</SelectItem><SelectItem value="30">Ultimi 30 giorni</SelectItem>
          <SelectItem value="prec">Mese precedente</SelectItem>
        </SelectContent>
      </Select>
      <Input type="date" className="h-8 w-36" value={dal} onChange={(e) => onCambia(e.target.value, al)} data-testid={`${testid}-dal`} />
      <Input type="date" className="h-8 w-36" value={al} onChange={(e) => onCambia(dal, e.target.value)} data-testid={`${testid}-al`} />
    </div>
  );
}

// Sprint CRM-2A — con il pannello chiuso restano i soli chip dei filtri attivi
// Sprint CRM-2A.1 — righe della scheda in colonna (la descrizione è sempre visibile); «Fonte» spenta di partenza
export const RIGHE_CARD = [["soggetto", "Soggetto"], ["telefono", "Telefono"], ["email", "Email"], ["contatore", "Opportunità del contatto"],
  ["prezzo_vendita", "Prezzo di vendita"], ["prezzo_web", "Prezzo web"], ["date", "Creata / Ultima modifica"],
  ["giorni", "Giorni nella fase"], ["fonte", "Fonte"], ["prossima", "Prossima attività"], ["icone", "Icone ed etichette"]];
export const CARD_PARTENZA = Object.fromEntries(RIGHE_CARD.map(([k]) => [k, k !== "fonte"]));
const LS_CARD = "crm-card-righe";
export const leggiCard = () => { try { return { ...CARD_PARTENZA, ...JSON.parse(localStorage.getItem(LS_CARD) || "{}") }; } catch { return CARD_PARTENZA; } };
export const salvaCard = (c) => { try { localStorage.setItem(LS_CARD, JSON.stringify(c)); } catch {} };

function PersonalizzaCard({ card, setCard }) {
  return (
    <div className="border-t pt-2" data-testid="crm-personalizza-card">
      <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Personalizza card opportunità</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {RIGHE_CARD.map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5 text-sm">
            <Checkbox checked={!!card[k]} onCheckedChange={(v) => setCard({ ...card, [k]: !!v })} data-testid={`crm-card-${k}`} />{l}</label>
        ))}
      </div>
    </div>
  );
}

export default function FiltriCrm({ f, setF, opzioni, vista, aperto = true, card, setCard }) {
  const set = (k, v) => setF({ ...f, [k]: v });
  const titolari = [...(opzioni?.nessun_titolare ? [{ id: "_nessuno", nome: "Nessun titolare" }] : []), ...(opzioni?.titolari || [])];
  const fonti = [...(opzioni?.fonti || []).map((x) => ({ ...x, nome: x.archiviata ? `${x.nome} (archiviata)` : x.nome })), { id: "_nessuno", nome: "Senza fonte" }];
  const motivi = [...(opzioni?.motivi || []), { id: "_nessuno", nome: "Senza motivo" }];
  if (!aperto) return <Chip f={f} setF={setF} titolari={titolari} fonti={fonti} fasi={opzioni?.fasi || []} motivi={motivi} vista={vista} />;
  return (
    <div className="space-y-2" data-testid="crm-filtri">
      <div className="flex flex-wrap items-center gap-3">
        <Input {...attrRicerca("crm-opp")} placeholder="Cerca nel CRM" value={f.q} onChange={(e) => set("q", e.target.value)}
          className="w-full sm:w-72" data-testid="crm-ricerca" />
        <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="crm-filtro-stati">
          {Object.entries(STATI_OPP).map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5">
              <Checkbox checked={f.stati.includes(k)} data-testid={`crm-filtro-stato-${k}`}
                onCheckedChange={() => set("stati", f.stati.includes(k) ? f.stati.filter((x) => x !== k) : [...f.stati, k])} />{l}
            </label>
          ))}
        </div>
        <Multi label="Titolare" scelte={titolari} valori={f.titolari} onCambia={(v) => set("titolari", v)} testid="crm-filtro-titolari" />
        <Multi label="Fonte" scelte={fonti} valori={f.fonti} onCambia={(v) => set("fonti", v)} testid="crm-filtro-fonti" />
        <Multi label="Etichetta" scelte={opzioni?.etichette || []} valori={f.etichette || []} onCambia={(v) => set("etichette", v)} testid="crm-filtro-etichette" />
        <Multi label="Motivo di perdita" scelte={motivi} valori={f.motivi || []} onCambia={(v) => set("motivi", v)} testid="crm-filtro-motivi" />
        <Select value={f.oltre_avviso || "_"} onValueChange={(v) => set("oltre_avviso", v === "_" ? "" : v)}>
          <SelectTrigger className="h-8 w-56" data-testid="crm-filtro-oltre-avviso"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_">Oltre l'avviso della fase: tutte</SelectItem><SelectItem value="si">Oltre l'avviso della fase: sì</SelectItem>
            <SelectItem value="no">Oltre l'avviso della fase: no</SelectItem></SelectContent>
        </Select>
        {vista === "righe" && <label className="flex items-center gap-1 text-sm">Senza attività da più di
          <Input type="number" min={1} className="w-20 h-8" value={f.senza_attivita_da || ""} onChange={(e) => set("senza_attivita_da", e.target.value)}
            data-testid="crm-filtro-senza-attivita" /> giorni</label>}
        {vista === "righe" && <Multi label="Fase" scelte={opzioni?.fasi || []} valori={f.fasi} onCambia={(v) => set("fasi", v)} testid="crm-filtro-fasi" />}
        <Select value={f.veicolo || "_"} onValueChange={(v) => set("veicolo", v === "_" ? "" : v)}>
          <SelectTrigger className="h-8 w-48" data-testid="crm-filtro-veicolo"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_">Veicolo agganciato: tutti</SelectItem><SelectItem value="si">Veicolo agganciato: sì</SelectItem>
            <SelectItem value="no">Veicolo agganciato: no</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Periodo label="Creazione" dal={f.creata_dal} al={f.creata_al} testid="crm-filtro-creazione" periodi={opzioni?.periodi}
          onCambia={(dal, al) => setF({ ...f, creata_dal: dal, creata_al: al })} />
        <Periodo label="Chiusura" dal={f.chiusa_dal} al={f.chiusa_al} testid="crm-filtro-chiusura" periodi={opzioni?.periodi}
          onCambia={(dal, al) => setF({ ...f, chiusa_dal: dal, chiusa_al: al })} />
      </div>
      <Chip f={f} setF={setF} titolari={titolari} fonti={fonti} fasi={opzioni?.fasi || []} motivi={motivi} vista={vista} />
      {card && vista === "colonne" && <PersonalizzaCard card={card} setCard={setCard} />}
    </div>
  );
}

// Un chip per filtro attivo: la stessa lista dà anche il numero sul pulsante «Filtri»
export function vociChip(f, titolari = [], fonti = [], fasi = [], vista, motivi = []) {
  const nome = (lista, id) => lista.find((x) => x.id === id)?.nome || "—";
  const chip = [];
  if (f.stati.join() !== STATI_PREDEFINITI.join()) f.stati.forEach((s) => chip.push({ k: "stati", v: s, t: `Stato: ${STATI_OPP[s]}` }));
  f.titolari.forEach((v) => chip.push({ k: "titolari", v, t: `Titolare: ${nome(titolari, v)}` }));
  f.fonti.forEach((v) => chip.push({ k: "fonti", v, t: `Fonte: ${nome(fonti, v)}` }));
  if (vista === "righe") f.fasi.forEach((v) => chip.push({ k: "fasi", v, t: `Fase: ${nome(fasi, v)}` }));
  (f.motivi || []).forEach((v) => chip.push({ k: "motivi", v, t: `Motivo di perdita: ${nome(motivi, v)}` }));
  if (f.oltre_avviso) chip.push({ k: "oltre_avviso", t: `Oltre l'avviso della fase: ${f.oltre_avviso === "si" ? "sì" : "no"}` });
  [["creata_dal", "Creata dal"], ["creata_al", "Creata al"], ["chiusa_dal", "Chiusa dal"], ["chiusa_al", "Chiusa al"]]
    .forEach(([k, l]) => f[k] && chip.push({ k, t: `${l} ${f[k].split("-").reverse().join("/")}` }));
  if (f.veicolo) chip.push({ k: "veicolo", t: `Veicolo agganciato: ${f.veicolo === "si" ? "sì" : "no"}` });
  if (f.q.trim()) chip.push({ k: "q", t: `Ricerca: ${f.q}` });
  return chip;
}
export const contaFiltri = (f, vista) => vociChip(f, [], [], [], vista).length;

function Chip({ f, setF, titolari, fonti, fasi, vista, motivi }) {
  const chip = vociChip(f, titolari, fonti, fasi, vista, motivi);
  const togli = (k, v) => setF({ ...f, [k]: Array.isArray(f[k]) ? f[k].filter((x) => x !== v) : "" });
  if (!chip.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="crm-chip">
      {chip.map((c, i) => (
        <span key={`${c.k}-${c.v || i}`} className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-900 px-2 py-0.5 text-xs" data-testid={`crm-chip-${c.k}-${c.v || ""}`}>
          {c.t}<button onClick={() => togli(c.k, c.v)} aria-label="Togli filtro"><X className="h-3 w-3" /></button>
        </span>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setF(FILTRI_VUOTI)} data-testid="crm-azzera-filtri">Azzera filtri</Button>
    </div>
  );
}
